/**
 * BaleAgeingPage — Phase 3A (Sec 13)
 * ─────────────────────────────────────────────────────────────────────────────
 * In-stock bales bucketed by age in days. Surfaces:
 *   - Age distribution histogram (5 buckets: 0-30 / 30-60 / 60-90 / 90-180 / >180)
 *   - Dead stock list (>180 days, never moved)
 *   - Re-test required list (>90 days since arrival — HVI may have drifted)
 *
 * Used by: store manager (clear old stock), QC (schedule re-test), procurement
 * (avoid over-ordering same variety).
 */
import { useState } from 'react'
import {
  Alert, Card, Col, Empty, Row, Select, Space, Statistic, Table, Tag,
  Tooltip, Typography,
} from 'antd'
import { ClockCircleOutlined, ReloadOutlined, WarningFilled } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import PageHeader from '@/shared/components/PageHeader'
import { baleAgeingApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'

const { Text } = Typography

type Plant = { id: string; name: string }

type Bucket = {
  label: string
  count: number
  weight_kg: number
  min_days: number
  max_days: number
}

type AgedBale = {
  bale_id: string
  bale_no: string
  age_days: number
  weight_kg: number
  qc_status: string
  cotton_grade: string | null
  supplier_name: string
  lot_no: string | null
}

type AgeingStats = {
  plant_id: string
  total_bales_in_stock: number
  total_weight_kg: number
  buckets: Bucket[]
  dead_stock_threshold_days: number
  retest_threshold_days: number
  dead_stock_count: number
  retest_required_count: number
  dead_stock_bales: AgedBale[]
  retest_required_bales: AgedBale[]
}

const BUCKET_COLOR = ['#52c41a', '#73d13d', '#faad14', '#fa8c16', '#ff4d4f']

function HistogramRow({ buckets, total }: { buckets: Bucket[]; total: number }) {
  if (total === 0) return null
  const pct = (n: number) => (n / total) * 100
  return (
    <div style={{
      display: 'flex', height: 32, borderRadius: 4, overflow: 'hidden',
      border: '1px solid #f0f0f0',
    }}>
      {buckets.map((b, i) => b.count > 0 ? (
        <Tooltip key={b.label} title={`${b.label}: ${b.count} bales · ${(b.weight_kg / 1000).toFixed(1)} T`}>
          <div style={{
            width: `${pct(b.count)}%`,
            background: BUCKET_COLOR[i] || '#888',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 600,
          }}>
            {pct(b.count) >= 10 ? `${b.label} ${b.count}` : ''}
          </div>
        </Tooltip>
      ) : null)}
    </div>
  )
}

export default function BaleAgeingPage() {
  const [plantId, setPlantId] = useState<string | undefined>()

  const plantsQ = useQuery<Plant[]>({
    queryKey: ['plants', 'all'],
    queryFn: async () => {
      const res = await plantsApi.list()
      const r = res as unknown as { items?: Plant[]; data?: Plant[] } | Plant[]
      if (Array.isArray(r)) return r
      return r.items ?? r.data ?? []
    },
  })

  if (!plantId && plantsQ.data && plantsQ.data.length > 0) {
    setPlantId(plantsQ.data[0].id)
  }

  const statsQ = useQuery<AgeingStats>({
    queryKey: ['bale-ageing', plantId],
    queryFn: () => baleAgeingApi.stats(plantId!),
    enabled: Boolean(plantId),
  })

  const data = statsQ.data

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Bale Ageing"
        subtitle="In-stock bales by age — find dead stock, schedule retests"
        extra={
          <Space>
            <Select
              value={plantId} onChange={setPlantId} placeholder="Plant"
              style={{ width: 180 }}
              options={(plantsQ.data || []).map(p => ({ label: p.name, value: p.id }))}
            />
            <ReloadOutlined onClick={() => statsQ.refetch()}
              spin={statsQ.isFetching}
              style={{ fontSize: 16, cursor: 'pointer' }} />
          </Space>
        }
      />

      {data && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Total bales in stock"
                  value={data.total_bales_in_stock}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Total weight"
                  value={(data.total_weight_kg / 1000).toFixed(2)}
                  suffix="T"
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={data.dead_stock_count > 0 ? { borderColor: '#ff4d4f' } : undefined}>
                <Statistic
                  title={`Dead stock (>${data.dead_stock_threshold_days}d)`}
                  value={data.dead_stock_count}
                  valueStyle={data.dead_stock_count > 0 ? { color: '#ff4d4f' } : undefined}
                  prefix={data.dead_stock_count > 0 ? <WarningFilled /> : null}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={data.retest_required_count > 0 ? { borderColor: '#faad14' } : undefined}>
                <Statistic
                  title={`Retest required (>${data.retest_threshold_days}d)`}
                  value={data.retest_required_count}
                  valueStyle={data.retest_required_count > 0 ? { color: '#faad14' } : undefined}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Age distribution" style={{ marginBottom: 16 }}>
            <HistogramRow buckets={data.buckets} total={data.total_bales_in_stock} />
            <Row gutter={8} style={{ marginTop: 12 }}>
              {data.buckets.map((b, i) => (
                <Col xs={12} md={4} key={b.label} flex="auto">
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <div style={{
                      width: 14, height: 14, borderRadius: 2,
                      background: BUCKET_COLOR[i] || '#888',
                      display: 'inline-block', marginRight: 6,
                    }} />
                    <Text style={{ fontSize: 11 }}>{b.label}</Text>
                    <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
                      {b.count}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {(b.weight_kg / 1000).toFixed(1)} T
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>

          {data.dead_stock_count > 0 && (
            <Alert
              type="error" showIcon
              style={{ marginBottom: 16 }}
              message={`${data.dead_stock_count} bales have been in stock over ${data.dead_stock_threshold_days} days`}
              description="Consider running them through a low-priority laydown, RTV, or supplier-claim flow."
            />
          )}

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Card title="Dead stock candidates" styles={{ body: { padding: 0 } }}>
                {data.dead_stock_bales.length === 0 ? (
                  <Empty description="No dead stock — clean!" style={{ padding: 32 }} />
                ) : (
                  <Table
                    rowKey="bale_id"
                    size="small"
                    dataSource={data.dead_stock_bales}
                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                    columns={[
                      { title: 'Bale', dataIndex: 'bale_no', width: 120 },
                      { title: 'Age', dataIndex: 'age_days', width: 80, align: 'right',
                        render: (v) => <Tag color="red">{v}d</Tag> },
                      { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
                      { title: 'Lot', dataIndex: 'lot_no', width: 100,
                        render: (v) => v ?? <Text type="secondary">—</Text> },
                      { title: 'Grade', dataIndex: 'cotton_grade', width: 70, align: 'center',
                        render: (v) => v ? <Tag>{v}</Tag> : '—' },
                    ]}
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Retest candidates" styles={{ body: { padding: 0 } }}>
                {data.retest_required_bales.length === 0 ? (
                  <Empty description="All bales fresh enough — no retests needed" style={{ padding: 32 }} />
                ) : (
                  <Table
                    rowKey="bale_id"
                    size="small"
                    dataSource={data.retest_required_bales}
                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                    columns={[
                      { title: 'Bale', dataIndex: 'bale_no', width: 120 },
                      { title: 'Age', dataIndex: 'age_days', width: 80, align: 'right',
                        render: (v) => <Tag color="orange">{v}d</Tag> },
                      { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
                      { title: 'Grade', dataIndex: 'cotton_grade', width: 70, align: 'center',
                        render: (v) => v ? <Tag>{v}</Tag> : '—' },
                    ]}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}
